// FinKloz — Option Grant Detail Panel
// Slide-out panel for viewing/editing a single option grant.
// Includes: editable fields, document attachment, vesting schedule, audit trail.

import React, { useState } from 'react';
import { sans, mono, green, greenLight, greenDark, red, amber, useTheme } from '../../tokens';
import { vestingSchedule, percentVested, vestedOptions } from '../../services/vestingEngine';

const teal = '#0D9488';

function fmt(n) {
  if (!n || n === 0) return '--';
  return Math.round(n).toLocaleString('en-CA');
}

function Field({ label, value, editing, onChange, type = 'text', options }) {
  const t = useTheme();
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 3 }}>{label}</div>
      {editing ? (
        options ? (
          <select value={value} onChange={e => onChange(e.target.value)}
            style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg }}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input type={type} value={value || ''} onChange={e => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
            style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg }} />
        )
      ) : (
        <div style={{ fontFamily: mono, fontSize: 12, color: t.textPrimary }}>{value || '--'}</div>
      )}
    </div>
  );
}

export default function OptionDetailPanel({ grant, asOfDate, onClose, onSave, onDelete, onDocumentsChange }) {
  const t = useTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...grant });
  const [showVesting, setShowVesting] = useState(false);
  const fileInputRef = React.useRef(null);
  const [localDocs, setLocalDocs] = useState(grant.documents || []);

  if (!grant) return null;

  const pctVest = percentVested(grant.vestStart, asOfDate);
  const numVested = vestedOptions(grant.options, grant.vestStart, asOfDate);
  const schedule = vestingSchedule(grant.options, grant.vestStart, grant.expiry);
  const hasDocuments = (grant.documents || []).length > 0;

  const handleSave = () => {
    if (onSave) onSave(draft);
    setEditing(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
      background: t.bgCard || '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 200, display: 'flex', flexDirection: 'column', fontFamily: sans,
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary }}>{grant.name}</div>
          <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 2 }}>{grant.rel} | {grant.cohort}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {!editing ? (
            <button onClick={() => setEditing(true)} style={{
              fontFamily: sans, fontSize: 11, padding: '4px 10px', borderRadius: 4,
              border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary, cursor: 'pointer',
            }}>Edit</button>
          ) : (
            <>
              <button onClick={() => { setDraft({ ...grant }); setEditing(false); }} style={{
                fontFamily: sans, fontSize: 11, padding: '4px 10px', borderRadius: 4,
                border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleSave} style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 4,
                border: `1px solid ${green}`, background: green, color: '#fff', cursor: 'pointer',
              }}>Save</button>
            </>
          )}
          <button onClick={onClose} style={{
            border: 'none', background: t.inputBg, borderRadius: 6, width: 28, height: 28,
            cursor: 'pointer', fontSize: 14, color: t.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>&times;</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {/* Vesting summary */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16,
          padding: '10px 12px', background: greenLight, borderRadius: 6,
        }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: green }}>{fmt(grant.options)}</div>
            <div style={{ fontFamily: sans, fontSize: 9, color: greenDark }}>Total Options</div>
          </div>
          <div>
            <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: green }}>{fmt(numVested)}</div>
            <div style={{ fontFamily: sans, fontSize: 9, color: greenDark }}>Vested</div>
          </div>
          <div>
            <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: amber }}>{fmt(grant.options - numVested)}</div>
            <div style={{ fontFamily: sans, fontSize: 9, color: greenDark }}>Unvested</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, marginBottom: 16 }}>
          <div style={{ height: 6, borderRadius: 3, background: green, width: `${pctVest * 100}%`, transition: 'width 0.3s' }} />
        </div>

        {/* Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <Field label="Employee" value={editing ? draft.name : grant.name} editing={editing}
            onChange={v => setDraft(d => ({ ...d, name: v }))} />
          <Field label="Relationship" value={editing ? draft.rel : grant.rel} editing={editing}
            onChange={v => setDraft(d => ({ ...d, rel: v }))} options={['Employee', 'Consultant', 'Founder', 'Advisor']} />
          <Field label="Grant Date" value={editing ? draft.grantDate : grant.grantDate} editing={editing} type="date"
            onChange={v => setDraft(d => ({ ...d, grantDate: v }))} />
          <Field label="Vesting Start" value={editing ? draft.vestStart : grant.vestStart} editing={editing} type="date"
            onChange={v => setDraft(d => ({ ...d, vestStart: v }))} />
          <Field label="Expiry Date" value={editing ? draft.expiry : grant.expiry} editing={editing} type="date"
            onChange={v => setDraft(d => ({ ...d, expiry: v }))} />
          <Field label="# Options" value={editing ? draft.options : grant.options} editing={editing} type="number"
            onChange={v => setDraft(d => ({ ...d, options: v }))} />
          <Field label="Exercise Price" value={editing ? draft.price : grant.price} editing={editing} type="number"
            onChange={v => setDraft(d => ({ ...d, price: v }))} />
          <Field label="Currency" value={editing ? draft.currency : grant.currency} editing={editing}
            onChange={v => setDraft(d => ({ ...d, currency: v }))} options={['USD', 'CAD']} />
          <Field label="Cohort" value={editing ? draft.cohort : grant.cohort} editing={editing}
            onChange={v => setDraft(d => ({ ...d, cohort: v }))} />
          <Field label="Fair Value (B-S)" value={editing ? draft.fv : grant.fv} editing={editing} type="number"
            onChange={v => setDraft(d => ({ ...d, fv: v }))} />
          <div style={{ gridColumn: '1 / -1', marginTop: 6, padding: '8px', background: t.bgSurface, borderRadius: 4, border: `1px solid ${t.borderLight}` }}>
            <div style={{ fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>Board Approval</div>
            <Field label="Board Resolution Reference" value={editing ? (draft.notes || '') : (grant.notes || '')} editing={editing}
              onChange={v => setDraft(d => ({ ...d, notes: v }))} />
          </div>
        </div>

        {/* Documents */}
        <div style={{ marginTop: 16, padding: '12px', background: t.bgSurface, borderRadius: 6, border: `1px solid ${t.border}` }}>
          <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>
            Documents {hasDocuments && `(${grant.documents.length})`}
          </div>
          {localDocs.length === 0 ? (
            <div style={{ fontFamily: sans, fontSize: 11, color: t.textMuted }}>No documents attached.</div>
          ) : (
            localDocs.map((doc, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
                borderBottom: `1px solid ${t.borderLight}`,
              }}>
                <span style={{ fontSize: 12 }}>&#128206;</span>
                <span style={{ fontFamily: sans, fontSize: 11, color: t.textPrimary, flex: 1 }}>{doc.name}</span>
                <span style={{ fontFamily: sans, fontSize: 9, color: t.textMuted }}>{doc.uploadedAt}</span>
                <button onClick={() => {
                  const updated = localDocs.filter((_, idx) => idx !== i);
                  setLocalDocs(updated);
                  if (onDocumentsChange) onDocumentsChange(updated);
                }} title="Remove document" style={{
                  border: 'none', background: 'transparent', color: red, fontSize: 14,
                  cursor: 'pointer', padding: '0 4px', lineHeight: 1, opacity: 0.6,
                }}>&times;</button>
              </div>
            ))
          )}
          <input ref={fileInputRef} type="file" style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.jpg,.png,.xlsx"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                const newDoc = { name: file.name, size: file.size, uploadedAt: new Date().toISOString().split('T')[0] };
                const updated = [...localDocs, newDoc];
                setLocalDocs(updated);
                if (onDocumentsChange) onDocumentsChange(updated);
                // TODO: upload to Supabase Storage
              }
            }}
          />
          <button onClick={() => fileInputRef.current?.click()} style={{
            fontFamily: sans, fontSize: 11, marginTop: 8, padding: '5px 10px',
            border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg,
            color: t.textSecondary, cursor: 'pointer', width: '100%',
          }}>
            + Attach Document
          </button>
        </div>

        {/* Vesting Schedule */}
        <div style={{ marginTop: 16 }}>
          <button onClick={() => setShowVesting(!showVesting)} style={{
            fontFamily: sans, fontSize: 12, fontWeight: 600, color: green,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}>
            {showVesting ? 'Hide' : 'Show'} Vesting Schedule
          </button>
          {showVesting && (
            <div style={{ marginTop: 8 }}>
              {/* Vesting Timeline Chart */}
              <div style={{ height: 120, position: 'relative', marginBottom: 12, background: t.bgSurface, borderRadius: 6, border: `1px solid ${t.borderLight}`, padding: '8px 4px 20px 30px' }}>
                <svg width="100%" height="100%" viewBox="0 0 340 90" preserveAspectRatio="none">
                  {/* Grid lines */}
                  {[0, 25, 50, 75, 100].map(pct => (
                    <line key={pct} x1="0" y1={90 - pct * 0.9} x2="340" y2={90 - pct * 0.9} stroke={t.borderLight} strokeWidth="0.5" />
                  ))}
                  {/* Vesting area */}
                  <path d={(() => {
                    if (schedule.length === 0) return '';
                    const vestStart = new Date(grant.vestStart);
                    const vestEnd = new Date(grant.vestStart);
                    vestEnd.setFullYear(vestEnd.getFullYear() + 4);
                    const totalDays = (vestEnd - vestStart) / 86400000;
                    const points = [{ x: 0, y: 90 }]; // start at 0%
                    // Cliff: 0% until cliff
                    const cliffDays = 365;
                    const cliffX = Math.min(cliffDays / totalDays * 340, 340);
                    points.push({ x: cliffX - 1, y: 90 }); // 0% just before cliff
                    // Then step up through schedule
                    schedule.forEach(s => {
                      const d = (new Date(s.date) - vestStart) / 86400000;
                      const x = Math.min(d / totalDays * 340, 340);
                      const y = 90 - s.vestedPct * 90;
                      points.push({ x, y });
                    });
                    points.push({ x: 340, y: points[points.length - 1].y });
                    points.push({ x: 340, y: 90 });
                    return 'M' + points.map(p => `${p.x},${p.y}`).join(' L');
                  })()} fill={greenLight} stroke={green} strokeWidth="1.5" />
                  {/* Today marker */}
                  {(() => {
                    const vestStart = new Date(grant.vestStart);
                    const vestEnd = new Date(grant.vestStart);
                    vestEnd.setFullYear(vestEnd.getFullYear() + 4);
                    const totalDays = (vestEnd - vestStart) / 86400000;
                    const todayDays = (new Date(asOfDate) - vestStart) / 86400000;
                    const x = Math.max(0, Math.min(todayDays / totalDays * 340, 340));
                    return (
                      <>
                        <line x1={x} y1="0" x2={x} y2="90" stroke={teal} strokeWidth="1.5" strokeDasharray="3,2" />
                        <circle cx={x} cy={90 - pctVest * 90} r="3" fill={teal} />
                      </>
                    );
                  })()}
                </svg>
                {/* Y axis labels */}
                <div style={{ position: 'absolute', left: 2, top: 6, fontFamily: mono, fontSize: 7, color: t.textMuted }}>100%</div>
                <div style={{ position: 'absolute', left: 2, bottom: 18, fontFamily: mono, fontSize: 7, color: t.textMuted }}>0%</div>
                {/* X axis labels */}
                <div style={{ position: 'absolute', left: 30, bottom: 2, fontFamily: mono, fontSize: 7, color: t.textMuted }}>
                  {grant.vestStart}
                </div>
                <div style={{ position: 'absolute', right: 4, bottom: 2, fontFamily: mono, fontSize: 7, color: t.textMuted }}>
                  {(() => { const d = new Date(grant.vestStart); d.setFullYear(d.getFullYear() + 4); return d.toISOString().split('T')[0]; })()}
                </div>
                <div style={{ position: 'absolute', right: 4, top: 4, fontFamily: sans, fontSize: 8, color: teal, fontWeight: 600 }}>
                  Today: {Math.round(pctVest * 100)}%
                </div>
              </div>

              {/* Schedule table */}
              {schedule.map((s, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '90px 1fr 60px 70px',
                  gap: 4, padding: '3px 0', borderBottom: `1px solid ${t.borderLight}`,
                  fontFamily: mono, fontSize: 10,
                }}>
                  <span style={{ color: t.textSecondary }}>{s.date}</span>
                  <span style={{ color: t.textPrimary }}>{s.event}</span>
                  <span style={{ color: green, textAlign: 'right' }}>{fmt(s.vestedQty)}</span>
                  <span style={{ color: t.textSecondary, textAlign: 'right' }}>{Math.round(s.vestedPct * 100)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note: Vesting pause and exercise/forfeit actions are in the People view */}

        {/* Delete */}
        {onDelete && (
          <div style={{ marginTop: 20, padding: '12px', borderTop: `1px solid ${t.border}` }}>
            <button onClick={() => { if (window.confirm(`Delete certificate for ${grant.name}? This cannot be undone.`)) { onDelete(grant.id); onClose(); } }} style={{
              fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 4, width: '100%',
              border: `1px solid ${red}`, background: '#FEF2F2', color: red, cursor: 'pointer',
            }}>Delete Certificate</button>
          </div>
        )}
      </div>
    </div>
  );
}
